import { getPageNameFromPathname } from '@/tracking/context/page-name';
import { parseNumber } from '@/utils/parse-number';

const GAME_ZONE_SELECTOR = '[data-t-zonemodule="game_zone"][data-t-zoneuid]';

const PERSONALIZED_ZONE_NAMES: Record<string, string> = {
    because_you_played: 'Because you played',
    games_recommender_configuration: 'Top Picks For You',
    similar_games: 'Similar Games'
};

const ZONE_LAYOUT_NAMES: Record<string, string> = {
    '2row': '2 Row',
    default: 'Default',
    one_row: '1 Row',
    single_row: '1 Row',
    spotlight: 'Featured',
    tall: 'Tall',
    two_row: '2 Row',
    vertical_featured_grid: 'Vertical Featured Grid'
};

const ZONE_TYPE_LAYOUT_NAMES: Record<string, string> = {
    animated_trailer: 'Animated Trailer',
    multi_zone: 'Multi zone',
    post_launch: 'Exclusive Post Launch'
};

type GameZoneInteractionAction = 'click' | 'impression' | 'scroll';

type GetGameZoneInteractionPropsParams = {
    action: GameZoneInteractionAction;
    element: HTMLElement | null;
    pathname: string;
    fallbackZoneName?: string;
    fallbackZoneType?: string;
    fallbackZoneId?: string | null;
    fallbackZoneLayout?: string | null;
    fallbackZonePosition?: string | number | null;
    ctaLabel?: string | null;
};

const getGameZoneSection = (element: HTMLElement | null): HTMLElement | null =>
    element?.closest<HTMLElement>(GAME_ZONE_SELECTOR) ?? null;

const EMOJI_PATTERN = /(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}|\p{Emoji_Modifier}|\u200D|\u20E3|\uFE0F)/gu;

const removeEmojis = (value: string): string => value.replace(EMOJI_PATTERN, '').trim().replace(/\s+/g, ' ');

const getStableZoneName = (zoneType: string, zoneName: string): string =>
    PERSONALIZED_ZONE_NAMES[zoneType] ?? removeEmojis(zoneName);

const getAnalyticsZoneLayout = (
    zoneType: string,
    zoneLayout: string | null,
    hasViewAll: boolean | null
): string | null => {
    const typeLayout = ZONE_TYPE_LAYOUT_NAMES[zoneType];
    if (typeLayout) {
        return typeLayout;
    }

    if (!zoneLayout) {
        return null;
    }

    if (zoneLayout === 'default' && hasViewAll === false) {
        return 'Default No View All';
    }

    return ZONE_LAYOUT_NAMES[zoneLayout] ?? zoneLayout;
};

export const isInTrackedGameZone = (element: HTMLElement | null): boolean => getGameZoneSection(element) !== null;

export const getGameZoneInteractionProps = ({
    action,
    element,
    pathname,
    fallbackZoneName = '',
    fallbackZoneType = '',
    fallbackZoneId = null,
    fallbackZoneLayout = null,
    fallbackZonePosition = null,
    ctaLabel = null
}: GetGameZoneInteractionPropsParams) => {
    const section = getGameZoneSection(element);
    const zoneType = section?.getAttribute('data-t-zonetype') || fallbackZoneType;
    const zoneName = section?.getAttribute('data-t-zonename') || fallbackZoneName;
    const zonePosition = parseNumber(section?.getAttribute('data-t-zoneposition') ?? fallbackZonePosition);
    const zoneLayout = section?.getAttribute('data-t-zonelayout') || fallbackZoneLayout;
    const hasViewAll = section ? section.querySelector('.category-view-all') !== null : null;

    return {
        action,
        automatedZone: null,
        ctaLabel,
        pageName: getPageNameFromPathname(pathname),
        zoneId: section?.getAttribute('data-t-zoneuid') || fallbackZoneId,
        zoneLayout: getAnalyticsZoneLayout(zoneType, zoneLayout, hasViewAll),
        zoneName: getStableZoneName(zoneType, zoneName),
        zonePosition: zonePosition === null ? null : zonePosition + 1,
        zoneType: 'game_zone'
    };
};
